/**
 * TrophyTokensVideoModal — iter87.533 (user-requested)
 *
 * Shared "How Trophy Tokens Work" walkthrough video (90s). Used across
 * several Tournament Admin entry points (tab top button, tile "?"
 * popout, Finalize-info popover link) and Help & FAQ — NOT the public
 * Pro+ carousel (removed in iter87.535; user: "instructional video,
 * should not be on the main landing page"). Mirrors the proven
 * `ProPlusVideoModalLink.jsx` Dialog pattern.
 *
 * Two modes:
 *  - Uncontrolled: pass `trigger` — the Dialog owns its own open state
 *    (used by the Pro+ carousel, via `TrophyTokensWatchButton`).
 *  - Controlled: pass `open`/`onOpenChange`, no `trigger` — lets a
 *    caller open this from OUTSIDE its own render tree. Required
 *    whenever the trigger button lives inside something that can
 *    unmount on click (e.g. iter87.533's regression: a trigger inside
 *    a closing Popover unmounted this Dialog along with it).
 *
 * Not re-hosted — streams directly from the provided asset URL.
 */
import { Play } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "./ui/dialog";

export const TROPHY_TOKENS_VIDEO_SRC = "https://customer-assets-cm19k8pv.emergentagent.net/wingman/436501d9-2931-4458-ad51-aeeab06280aa/attachments/1601d0bcb39a4bfa81c049d7513c56ab_trophy_tokens_v2_web.mp4";
export const TROPHY_TOKENS_VIDEO_POSTER = "https://customer-assets-cm19k8pv.emergentagent.net/wingman/436501d9-2931-4458-ad51-aeeab06280aa/attachments/0c0766fecb9647b9af9e540a6d09386a_m13_00.jpg";

export default function TrophyTokensVideoModal({ trigger, open, onOpenChange, testId = "trophy-tokens-video-modal" }) {
    const dialogProps = trigger ? {} : { open, onOpenChange };
    return (
        <Dialog {...dialogProps}>
            {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
            <DialogContent
                className="max-w-3xl w-[95vw] bg-[#0a0a0a] border-sky-400/50 p-4 sm:p-6 z-[70] [&>button]:w-8 [&>button]:h-8 [&>button]:flex [&>button]:items-center [&>button]:justify-center"
                data-testid={testId}
            >
                <DialogTitle className="text-white text-sm font-black uppercase tracking-widest text-left mb-1">
                    How Trophy Tokens Work
                </DialogTitle>
                <div
                    className="relative w-full rounded-xl overflow-hidden border-2 border-sky-400/60 bg-black shadow-[0_0_40px_rgba(56,189,248,0.35)]"
                    style={{ aspectRatio: "16 / 9" }}
                    data-testid={`${testId}-player`}
                >
                    <video
                        className="w-full h-full object-cover"
                        controls
                        playsInline
                        preload="metadata"
                        poster={TROPHY_TOKENS_VIDEO_POSTER}
                        aria-label="How Trophy Tokens work, 90 second walkthrough"
                        data-testid={`${testId}-element`}
                    >
                        <source src={TROPHY_TOKENS_VIDEO_SRC} type="video/mp4" />
                        Your browser doesn't support embedded video — you can still{" "}
                        <a href={TROPHY_TOKENS_VIDEO_SRC} className="text-sky-400 underline">
                            download the clip
                        </a>
                        .
                    </video>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export function TrophyTokensWatchButton({ label = "Watch how it works", testId, className = "" }) {
    return (
        <TrophyTokensVideoModal
            testId={testId ? `${testId}-modal` : undefined}
            trigger={
                <button
                    type="button"
                    data-testid={testId}
                    className={`inline-flex items-center gap-1.5 text-xs font-bold text-sky-400 hover:text-sky-300 transition-colors ${className}`}
                >
                    <span className="w-5 h-5 rounded-full bg-sky-400 flex items-center justify-center shrink-0">
                        <Play className="w-2.5 h-2.5 text-black ml-0.5" fill="currentColor" />
                    </span>
                    {label}
                </button>
            }
        />
    );
}
